import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { ContextTypeCheckboxList } from "@/lib/company-filter/modules/intent/components/filters/ContextTypeCheckboxList";
import type {
  ScoringType,
  ContextTypeBand,
  TopicsFilter,
} from "@/lib/scoring/types";
import type { CachedTopic } from "@/types/intent";
import { IntentTopicsSection } from "./IntentTopicsSection";

const DEFAULT_TOPICS: TopicsFilter = {
  ids: [],
  inclusionMethod: "ANY_PRESENT",
};

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: ContextTypeBand = {
  contextTypeIds: [],
  score: 0,
  topics: DEFAULT_TOPICS,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as ContextTypeBand[];
  if (typed.length === 0) return true;
  const topicIds = typed[0]?.topics?.ids ?? [];
  if (topicIds.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as ContextTypeBand;
  const count = b.contextTypeIds?.length ?? 0;
  if (count === 0) return "—";
  return `${count} context type${count !== 1 ? "s" : ""}`;
}

// ── Validation ────────────────────────────────────────────────────────────

function getBandError(
  band: ContextTypeBand,
  others: ContextTypeBand[]
): string | null {
  if (!band.score) return "Points can't be empty";
  if (!band.contextTypeIds?.length) return "Select at least one context type";

  // Check for duplicates across bands
  const otherIds = new Set(others.flatMap((o) => o.contextTypeIds ?? []));
  const hasDuplicate = (band.contextTypeIds ?? []).some((id) =>
    otherIds.has(id)
  );
  if (hasDuplicate) return "Context types can't be the same across bands";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const ContextTypeEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.contextType ?? [];

  const [topicsCache, setTopicsCache] = useState<CachedTopic[]>([]);
  const [topics, setTopics] = useState<TopicsFilter>(
    bands[0]?.topics ?? DEFAULT_TOPICS
  );

  const handleTopicsChange = useCallback(
    (newTopics: TopicsFilter, cache: CachedTopic[]) => {
      setTopics(newTopics);
      setTopicsCache(cache);
      onChange({
        ...criterion,
        contextType: bands.map((b) => ({ ...b, topics: newTopics })),
      });
    },
    [bands, criterion, onChange]
  );

  const updateBand = (index: number, patch: Partial<ContextTypeBand>) => {
    onChange({
      ...criterion,
      contextType: bands.map((b, i) =>
        i === index ? { ...b, ...patch, topics } : { ...b, topics }
      ),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      contextType: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, topics })),
    });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      contextType: [
        ...bands.map((b) => ({ ...b, topics })),
        { contextTypeIds: [], score: 0, topics },
      ],
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <IntentTopicsSection
        topics={topics}
        topicsCache={topicsCache}
        onChange={handleTopicsChange}
      />

      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          (band.contextTypeIds?.length ?? 0) > 0 || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: error ? "error.main" : "divider",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>
                Context Type
              </Typography>
              <Tooltip
                title="Select the buying context types to score. Hover over each option to see its description."
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
              </Tooltip>
            </Stack>
            <ContextTypeCheckboxList
              selectedIds={band.contextTypeIds ?? []}
              onChange={(ids) => updateBand(i, { contextTypeIds: ids })}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                error={band.score === 0 && isDirty}
                value={band.score === 0 ? "" : band.score}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 80 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
